import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabase'
import Topbar from '../components/Topbar'
import AdBoostCard from '../components/AdBoostCard'
import { useAuth } from '../context/AuthContext'
import { getCurrencySymbol } from '../utils/currencies'
import { useTranslation } from '../i18n/LanguageContext'
import { QUOTES } from '../utils/motivationalQuotes'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts'

const CHART_COLORS = ['#0EA5E9','#2DD4B0','#F59E0B','#7C3AED','#EF4444','#06B6D4','#F97316','#84CC16','#A855F7','#EC4899']

const STATUS_STYLES = {
  received:     { bg:'var(--primary-light)',   text:'var(--primary)' },
  pending:      { bg:'var(--warning-light)',   text:'var(--warning)' },
  confirmed:    { bg:'#FEF3C7',               text:'#92400E' },
  processing:   { bg:'var(--success-light)',   text:'var(--success)' },
  with_courier: { bg:'#F5F3FF',               text:'#6D28D9' },
  shipped:      { bg:'var(--success-light)',   text:'var(--success)' },
  closed:       { bg:'var(--bg-muted)',         text:'var(--text-muted)' },
  return:       { bg:'var(--error-light)',      text:'var(--error)' },
}
const STATUS_LABELS = { received:'Received',pending:'Pending',confirmed:'Confirmed',processing:'Processing',with_courier:'Courier',shipped:'Shipped',closed:'Closed',return:'Return' }
const DASH_PAGE_SIZE = 10

const BROAD_SOURCE_MAP = {
  online: { label:'Online', color:'#0EA5E9', bg:'rgba(14,165,233,0.12)', icon:'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9', sources:['facebook','instagram','tiktok','website','whatsapp','order form'] },
  phone:  { label:'Phone',  color:'#10B981', bg:'rgba(16,185,129,0.12)', icon:'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z', sources:['phone call'] },
  pos:    { label:'POS',    color:'#7C3AED', bg:'rgba(124,58,237,0.12)', icon:'M9 7H6a2 2 0 00-2 2v9a2 2 0 002 2h9a2 2 0 002-2v-3M16 5a2 2 0 112 2M16 5H9m7 0l-7 7', sources:['walk-in','at counter','pos'] },
  event:  { label:'Event',  color:'#F59E0B', bg:'rgba(245,158,11,0.12)', icon:'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', sources:['event','fair'] },
}
function getBroadSource(src) {
  if (!src) return null
  const l = src.toLowerCase()
  for (const v of Object.values(BROAD_SOURCE_MAP)) { if (v.sources.some(s=>l.includes(s))) return v }
  return null
}

function SourceBadge({ orderSource }) {
  const b = getBroadSource(orderSource)
  if (!b) return null
  return (
    <div title={b.label} style={{ width:'20px',height:'20px',display:'inline-flex',alignItems:'center',justifyContent:'center',borderRadius:'5px',background:b.bg,flexShrink:0 }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={b.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d={b.icon}/></svg>
    </div>
  )
}

function Icon({ path, size=16, color='var(--text-muted)', sw=1.8 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"><path d={path}/></svg>
}

function FlatIcon({ path, size=16, color='var(--text-muted)' }) {
  return <svg width={size} height={size} viewBox="0 0 20 20" fill={color}><path fillRule="evenodd" d={path} clipRule="evenodd"/></svg>
}

const IC = {
  revenue:   'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  orders:    'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z',
  trending:  'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
  chart:     'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  clock:     'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  warning:   'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  return:    'M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6',
  plus:      'M12 4v16m8-8H4',
  arrow:     'M9 18l6-6-6-6',
  check:     'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  request:   'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  inventory: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
  analytics: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  upArrow:   'M5 10l7-7m0 0l7 7m-7-7v18',
  downArrow: 'M19 14l-7 7m0 0l-7-7m7 7V3',
  search:    'M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z',
  chevL:     'M15 18l-6-6 6-6',
  chevR:     'M9 18l6-6-6-6',
  users:     'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  target:    'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  lightbulb: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function Sk({ w='100%', h=16, r=6 }) {
  return <div className="pc-skeleton" style={{ width:w, height:h, borderRadius:r }} />
}

// ── Animated counter hook ─────────────────────────────────────────────────────
function useCountUp(target, duration=800) {
  const [val, setVal] = useState(0)
  const prev = useRef(0)
  useEffect(() => {
    if (target === 0) { setVal(0); return }
    const start = prev.current
    const startTime = performance.now()
    const tick = (now) => {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3) // ease out cubic
      setVal(Math.round(start + (target - start) * eased))
      if (progress < 1) requestAnimationFrame(tick)
      else prev.current = target
    }
    requestAnimationFrame(tick)
  }, [target, duration])
  return val
}

// ── Hero KPI card — gradient background, large number ────────────────────────
function HeroKpiCard({ label, value, rawValue, delta, deltaUp, icon, gradient, loading }) {
  const counted = useCountUp(loading ? 0 : rawValue || 0)
  const displayValue = loading ? null : (rawValue !== undefined ? value.replace(/[\d,]+/, counted.toLocaleString()) : value)

  return (
    <div className="pc-card-hover"
      style={{ borderRadius:'var(--radius-lg)', padding:'22px 24px', background:gradient, position:'relative', overflow:'hidden', cursor:'default', transition:'var(--transition-slow)', boxShadow:'0 4px 24px rgba(0,0,0,0.12)' }}>
      {/* Background decoration */}
      <div style={{ position:'absolute', top:'-20px', right:'-20px', width:'80px', height:'80px', borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }} />
      <div style={{ position:'absolute', bottom:'-30px', right:'20px', width:'120px', height:'120px', borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }} />

      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'14px', position:'relative' }}>
        <span style={{ fontSize:'11.5px', fontWeight:600, color:'rgba(255,255,255,0.7)', textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:'var(--font-ui)' }}>{label}</span>
        <div style={{ width:'34px', height:'34px', borderRadius:'10px', background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center' }}>
          {icon}
        </div>
      </div>

      <div style={{ fontSize:'36px', fontWeight:900, color:'#FFFFFF', letterSpacing:'-1px', lineHeight:1, marginBottom:'10px', fontFamily:'var(--font-ui)' }}>
        {loading ? <Sk w="70%" h={36} r={8} /> : displayValue}
      </div>

      {loading ? <Sk w="50%" h={13} r={4} /> : delta && (
        <div style={{ display:'flex', alignItems:'center', gap:'5px' }}>
          <div style={{ width:'18px', height:'18px', borderRadius:'50%', background: deltaUp ? 'rgba(52,211,153,0.25)' : 'rgba(252,165,165,0.25)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Icon path={deltaUp ? IC.upArrow : IC.downArrow} color={deltaUp ? '#34D399' : '#FCA5A5'} size={10} sw={2.5} />
          </div>
          <span style={{ fontSize:'12px', fontWeight:500, color: deltaUp ? '#34D399' : '#FCA5A5', fontFamily:'var(--font-body)' }}>{delta}</span>
        </div>
      )}
    </div>
  )
}

// ── Regular KPI card — white/surface background ───────────────────────────────
function KpiCard({ label, value, rawValue, delta, deltaUp, icon, accentColor, loading }) {
  const counted = useCountUp(loading ? 0 : rawValue || 0)
  const displayValue = loading ? null : (rawValue !== undefined ? String(counted) : value)

  return (
    <div className="pc-card pc-card-hover" style={{ padding:'20px 22px' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'12px' }}>
        <span style={{ fontSize:'11.5px', fontWeight:500, color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{label}</span>
        <div style={{ width:'32px', height:'32px', borderRadius:'var(--radius-md)', background:accentColor+'18', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          {icon}
        </div>
      </div>
      <div style={{ fontSize:'28px', fontWeight:800, color:'var(--text-primary)', letterSpacing:'-0.5px', marginBottom:'8px', lineHeight:1, fontFamily:'var(--font-ui)' }}>
        {loading ? <Sk w="60%" h={28} r={6} /> : displayValue}
      </div>
      {loading ? <Sk w="50%" h={12} r={4} /> : delta && (
        <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
          <Icon path={deltaUp ? IC.upArrow : IC.downArrow} color={deltaUp?'var(--success)':'var(--error)'} size={11} sw={2.5} />
          <span style={{ fontSize:'11.5px', fontWeight:500, color:deltaUp?'var(--success)':'var(--error)', fontFamily:'var(--font-body)' }}>{delta}</span>
        </div>
      )}
    </div>
  )
}

// ── Secondary metric tile ─────────────────────────────────────────────────────
function MetricTile({ label, value, rawValue, icon, accentColor, onClick, loading, suffix='' }) {
  const counted = useCountUp(loading ? 0 : rawValue || 0)
  const displayValue = loading ? null : (rawValue !== undefined ? counted + suffix : value + suffix)

  return (
    <div onClick={onClick} className="pc-card pc-card-hover"
      style={{ padding:'16px 18px', borderLeft:'3px solid '+accentColor, cursor:'pointer' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
        <span style={{ fontSize:'11px', fontWeight:500, color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{label}</span>
        <div style={{ width:'24px', height:'24px', borderRadius:'6px', background:accentColor+'18', display:'flex', alignItems:'center', justifyContent:'center' }}>
          {icon}
        </div>
      </div>
      <div style={{ fontSize:'24px', fontWeight:800, color:'var(--text-primary)', letterSpacing:'-0.3px', lineHeight:1, fontFamily:'var(--font-ui)' }}>
        {loading ? <Sk w="40%" h={24} r={4} /> : displayValue}
      </div>
    </div>
  )
}

// ── Quick action button ───────────────────────────────────────────────────────
function QuickAction({ label, sub, path, icon, accentColor, onClick }) {
  return (
    <button onClick={onClick} className="pc-btn"
      style={{ display:'flex', alignItems:'center', gap:'12px', padding:'11px 14px', borderRadius:'var(--radius-md)', background:'var(--bg-muted)', border:'1px solid var(--border)', cursor:'pointer', width:'100%', textAlign:'left', fontFamily:'var(--font-ui)', transition:'var(--transition-base)' }}
      onMouseEnter={e=>{ e.currentTarget.style.background='var(--bg-card)'; e.currentTarget.style.borderColor='var(--border-strong)'; e.currentTarget.style.boxShadow='var(--shadow-md)' }}
      onMouseLeave={e=>{ e.currentTarget.style.background='var(--bg-muted)'; e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.boxShadow='none' }}>
      <div style={{ width:'34px', height:'34px', borderRadius:'var(--radius-md)', background:accentColor+'18', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        {icon}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)', fontFamily:'var(--font-ui)' }}>{label}</div>
        <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginTop:'1px', fontFamily:'var(--font-body)' }}>{sub}</div>
      </div>
      <Icon path={IC.arrow} color="var(--text-muted)" size={13} sw={2} />
    </button>
  )
}

// ── Card shell ────────────────────────────────────────────────────────────────
function DCard({ children, style={} }) {
  return <div className="pc-card" style={{ overflow:'hidden', ...style }}>{children}</div>
}

function DCardHeader({ title, sub, action }) {
  return (
    <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
      <div>
        <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-ui)' }}>{title}</div>
        {sub && <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginTop:'2px', fontFamily:'var(--font-body)' }}>{sub}</div>}
      </div>
      {action}
    </div>
  )
}

function TLink({ children, onClick }) {
  return (
    <button onClick={onClick} style={{ fontSize:'12px', fontWeight:600, color:'var(--primary)', background:'none', border:'none', cursor:'pointer', fontFamily:'var(--font-ui)', padding:0 }}
      onMouseEnter={e=>e.currentTarget.style.color='var(--primary-hover)'}
      onMouseLeave={e=>e.currentTarget.style.color='var(--primary)'}>
      {children}
    </button>
  )
}

// ── Chart tooltip ─────────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label, sym }) {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'10px 14px', boxShadow:'var(--shadow-lg)', fontFamily:'var(--font-body)', minWidth:'140px' }}>
      <div style={{ fontSize:'11px', fontWeight:700, color:'var(--text-muted)', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</div>
      {payload.map(p=>(
        <div key={p.dataKey} style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'3px' }}>
          <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:p.color }} />
          <span style={{ fontSize:'12px', color:'var(--text-muted)' }}>{p.dataKey==='revenue'?'Revenue':'Orders'}:</span>
          <span style={{ fontSize:'12px', fontWeight:700, color:'var(--text-primary)' }}>
            {p.dataKey==='revenue' ? sym+Number(p.value).toLocaleString() : p.value}
          </span>
        </div>
      ))}
    </div>
  )
}

// ── Top products bar chart ────────────────────────────────────────────────────
function TopProductsChart({ data, loading, sym, metric, setMetric }) {
  const [animated, setAnimated] = useState(false)
  const ref = useRef()

  useEffect(() => {
    if (!loading && data.length > 0) { const t=setTimeout(()=>setAnimated(true),120); return ()=>clearTimeout(t) }
  }, [loading, data])

  const max = data.length > 0 ? Math.max(...data.map(d=>metric==='revenue'?d.revenue:d.units)) : 1

  return (
    <div>
      <div style={{ display:'flex', gap:'4px', marginBottom:'16px' }}>
        {[{key:'revenue',label:'Revenue'},{key:'units',label:'Units Sold'}].map(opt=>(
          <button key={opt.key} onClick={()=>{setMetric(opt.key);setAnimated(false);setTimeout(()=>setAnimated(true),60)}}
            style={{ padding:'5px 14px', borderRadius:'var(--radius-sm)', border:'none', fontSize:'12px', fontWeight:600, cursor:'pointer', background:metric===opt.key?'var(--primary)':'var(--bg-muted)', color:metric===opt.key?'white':'var(--text-muted)', fontFamily:'var(--font-ui)', transition:'var(--transition-base)' }}>
            {opt.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
          {[...Array(6)].map((_,i)=>(
            <div key={i} style={{ display:'flex', alignItems:'center', gap:'12px' }}>
              <Sk w={100} h={12} r={4} />
              <div style={{ flex:1 }}><Sk h={22} r={5} /></div>
              <Sk w={64} h={12} r={4} />
            </div>
          ))}
        </div>
      ) : data.length===0 ? (
        <div style={{ padding:'24px', textAlign:'center', color:'var(--text-muted)', fontSize:'13px', fontFamily:'var(--font-body)' }}>No sales data yet</div>
      ) : (
        <div ref={ref} style={{ display:'flex', flexDirection:'column', gap:'9px' }}>
          {data.map((item,i)=>{
            const val = metric==='revenue'?item.revenue:item.units
            const pct = max>0?(val/max)*100:0
            const color = CHART_COLORS[i%CHART_COLORS.length]
            return (
              <div key={item.name} style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                <div style={{ width:'3px', height:'20px', background:color, borderRadius:'2px', flexShrink:0 }} />
                <div style={{ width:'108px', fontSize:'12px', fontWeight:500, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flexShrink:0, fontFamily:'var(--font-body)' }} title={item.name}>
                  {item.name.length>15?item.name.slice(0,15)+'…':item.name}
                </div>
                <div style={{ flex:1, height:'22px', background:'var(--bg-muted)', borderRadius:'5px', overflow:'hidden' }}>
                  <div style={{ height:'100%', borderRadius:'5px', background:`linear-gradient(90deg,${color}ee,${color}77)`, width:animated?pct+'%':'0%', transition:`width 0.65s cubic-bezier(0.4,0,0.2,1) ${i*0.06}s`, display:'flex', alignItems:'center', paddingLeft:'8px' }}>
                    {pct>25&&<span style={{ fontSize:'10.5px', fontWeight:700, color:'rgba(255,255,255,0.9)', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{metric==='revenue'?sym+Number(val).toLocaleString():val+' units'}</span>}
                  </div>
                </div>
                <div style={{ fontSize:'12px', fontWeight:700, color:'var(--text-primary)', flexShrink:0, width:'72px', textAlign:'right', fontFamily:'var(--font-body)' }}>
                  {metric==='revenue'?sym+Number(val).toLocaleString():val+' units'}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Donut center label ────────────────────────────────────────────────────────
function DonutCenter({ cx, cy, total }) {
  return (
    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontFamily="var(--font-ui)">
      <tspan x={cx} dy="-7" fontSize="20" fontWeight="900" fill="var(--text-primary)">{total}</tspan>
      <tspan x={cx} dy="19" fontSize="10" fill="var(--text-muted)">total orders</tspan>
    </text>
  )
}

function getTimeAgo(date) {
  const s = Math.floor((new Date()-date)/1000)
  if (s<60) return 'just now'
  if (s<3600) return Math.floor(s/60)+'m ago'
  if (s<86400) return Math.floor(s/3600)+'h ago'
  if (s<604800) return Math.floor(s/86400)+'d ago'
  return date.toLocaleDateString('en-BD',{day:'numeric',month:'short'})
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard() {
  const navigate = useNavigate()
  const { t, lang } = useTranslation()
  const { seller } = useAuth()

  const currency = seller?.seller_currency || 'BDT'
  const sym = getCurrencySymbol(currency)

  // A fresh motivational quote each time the Dashboard loads
  const [quote] = useState(() => QUOTES[Math.floor(Math.random()*QUOTES.length)])

  const ACTIVITY_META = {
    status_changed:     { color:'var(--primary)',  label:t('dashboard.status_updated') },
    order_edited:       { color:'#7C3AED',          label:t('dashboard.order_edited') },
    order_deleted:      { color:'var(--error)',      label:t('dashboard.order_deleted') },
    dash_order_created: { color:'var(--warning)',    label:t('dashboard.dash_order_created') },
    product_edited:     { color:'var(--success)',    label:t('dashboard.product_updated') },
    product_deleted:    { color:'var(--error)',      label:t('dashboard.product_deleted') },
    stock_updated:      { color:'#F59E0B',            label:t('dashboard.stock_updated') },
  }

  const [stats, setStats] = useState({ todayRevenue:0,monthRevenue:0,todayOrders:0,monthOrders:0,pending:0,returns:0,lowStock:0,pendingRequests:0,yesterdayRevenue:0,lastMonthRevenue:0,yesterdayOrders:0,lastMonthOrders:0,avgOrderValue:0,returnRate:0,newCustomers:0 })
  const [allOrders, setAllOrders]           = useState([])
  const [lowStockItems, setLowStockItems]   = useState([])
  const [chartData, setChartData]           = useState([])
  const [chartRange, setChartRange]         = useState('6m')
  const [sourceData, setSourceData]         = useState([])
  const [topProducts, setTopProducts]       = useState([])
  const [loading, setLoading]               = useState(true)
  const [recentActivity, setRecentActivity] = useState([])
  const [topCustomers, setTopCustomers]     = useState([])
  const [dashSearch, setDashSearch]         = useState('')
  const [dashPage, setDashPage]             = useState(1)
  const [productMetric, setProductMetric]   = useState('revenue')
  const [goalData, setGoalData]             = useState(null)

  useEffect(() => {
    if (seller?.id) {
      if (seller.setup_completed === false) { navigate('/setup'); return }
      fetchDashboardData()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seller])

  const getChartStart = useCallback(() => {
    const now = new Date()
    const m = chartRange==='1m'?1:chartRange==='3m'?3:6
    const d = new Date(now); d.setMonth(d.getMonth()-m+1); d.setDate(1); d.setHours(0,0,0,0)
    return d
  }, [chartRange])

  useEffect(() => {
    if (!loading && allOrders.length > 0) rebuildChart()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRange])

  const rebuildChart = useCallback(() => {
    const start = getChartStart()
    const raw = allOrders.filter(o=>new Date(o.created_at)>=start)
    const map = {}
    raw.forEach(o=>{
      const d=new Date(o.created_at)
      const key=d.toLocaleDateString('en-BD',{month:'short',year:'2-digit'})
      if(!map[key]) map[key]={month:key,revenue:0,orders:0}
      map[key].revenue+=Number(o.total_amount||0)
      map[key].orders+=1
    })
    setChartData(Object.values(map))
  }, [allOrders, getChartStart])

  const fetchDashboardData = async () => {
    setLoading(true)
    const now              = new Date()
    const startOfDay       = new Date(now); startOfDay.setHours(0,0,0,0)
    const startOfMonth     = new Date(now); startOfMonth.setDate(1); startOfMonth.setHours(0,0,0,0)
    const startOfYesterday = new Date(startOfDay); startOfYesterday.setDate(startOfYesterday.getDate()-1)
    const startOfLastMonth = new Date(startOfMonth); startOfLastMonth.setMonth(startOfLastMonth.getMonth()-1)
    const endOfLastMonth   = new Date(startOfMonth)
    const sixMonthsAgo     = new Date(now); sixMonthsAgo.setMonth(sixMonthsAgo.getMonth()-5); sixMonthsAgo.setDate(1); sixMonthsAgo.setHours(0,0,0,0)
    const startOfThisMonthForCustomers = new Date(now); startOfThisMonthForCustomers.setDate(1); startOfThisMonthForCustomers.setHours(0,0,0,0)

    const [allOrdersRes,todayRes,yesterdayRes,monthRes,lastMonthRes,productsRes,requestsRes,lowStockRes,chartRes,recentOrdersRes,newCustomersRes,goalRes] = await Promise.all([
      supabase.from('orders').select('id,status,payment_status,order_source').eq('seller_id',seller.id),
      supabase.from('orders').select('id,total_amount').eq('seller_id',seller.id).gte('created_at',startOfDay.toISOString()),
      supabase.from('orders').select('id,total_amount').eq('seller_id',seller.id).gte('created_at',startOfYesterday.toISOString()).lt('created_at',startOfDay.toISOString()),
      supabase.from('orders').select('id,total_amount').eq('seller_id',seller.id).gte('created_at',startOfMonth.toISOString()),
      supabase.from('orders').select('id,total_amount').eq('seller_id',seller.id).gte('created_at',startOfLastMonth.toISOString()).lt('created_at',endOfLastMonth.toISOString()),
      supabase.from('products').select('stock').eq('seller_id',seller.id),
      supabase.from('customer_order_requests').select('id').eq('seller_id',seller.id).eq('status','submitted'),
      supabase.from('products').select('id,name,stock,product_code').eq('seller_id',seller.id).lte('stock',5).order('stock',{ascending:true}).limit(6),
      supabase.from('orders').select('created_at,total_amount,id,status,payment_status,order_source,product_name,product_code,order_code,customers(name,phone)').eq('seller_id',seller.id).gte('created_at',sixMonthsAgo.toISOString()).order('created_at',{ascending:false}).limit(500),
      supabase.from('orders').select('id,total_amount,created_at,customers(name,phone),product_name,product_code,order_code,status,payment_status,order_source').eq('seller_id',seller.id).order('created_at',{ascending:false}).limit(200),
      supabase.from('customers').select('id',{count:'exact',head:true}).eq('seller_id',seller.id).gte('created_at',startOfThisMonthForCustomers.toISOString()),
      supabase.from('seller_goals').select('*').eq('seller_id',seller.id).eq('month',now.getMonth()+1).eq('year',now.getFullYear()).single(),
    ])

    const todayOrds    = todayRes.data     || []
    const yestOrds     = yesterdayRes.data || []
    const monthOrds    = monthRes.data     || []
    const lastMonOrds  = lastMonthRes.data || []
    const allOrd       = allOrdersRes.data || []

    const todayRev     = todayOrds.reduce((s,o)=>s+Number(o.total_amount||0),0)
    const yestRev      = yestOrds.reduce((s,o)=>s+Number(o.total_amount||0),0)
    const monthRev     = monthOrds.reduce((s,o)=>s+Number(o.total_amount||0),0)
    const lastMonRev   = lastMonOrds.reduce((s,o)=>s+Number(o.total_amount||0),0)
    const avgOrderVal  = monthOrds.length>0 ? Math.round(monthRev/monthOrds.length) : 0
    const totalOrd     = allOrd.length
    const returnCount  = allOrd.filter(o=>o.status==='return').length
    const returnRate   = totalOrd>0 ? ((returnCount/totalOrd)*100).toFixed(1) : 0

    setStats({ todayRevenue:todayRev,monthRevenue:monthRev,todayOrders:todayOrds.length,monthOrders:monthOrds.length, pending:allOrd.filter(o=>o.status==='pending'||o.status==='received').length, returns:returnCount,lowStock:(productsRes.data||[]).filter(p=>p.stock<=5).length, pendingRequests:(requestsRes.data||[]).length,yesterdayRevenue:yestRev,lastMonthRevenue:lastMonRev, yesterdayOrders:yestOrds.length,lastMonthOrders:lastMonOrds.length,avgOrderValue:avgOrderVal, returnRate:Number(returnRate),newCustomers:newCustomersRes.count||0 })
    setAllOrders(recentOrdersRes.data || [])
    setLowStockItems(lowStockRes.data || [])
    setGoalData(goalRes.data || null)

    // Chart
    const chartRaw = chartRes.data || []
    const map = {}
    chartRaw.forEach(o=>{
      const d=new Date(o.created_at)
      const key=d.toLocaleDateString('en-BD',{month:'short',year:'2-digit'})
      if(!map[key]) map[key]={month:key,revenue:0,orders:0}
      map[key].revenue+=Number(o.total_amount||0); map[key].orders+=1
    })
    setChartData(Object.values(map).slice(-6))

    // Source
    const srcMap = {}
    allOrd.forEach(o=>{ const s=o.order_source||'Other'; srcMap[s]=(srcMap[s]||0)+1 })
    setSourceData(Object.entries(srcMap).map(([name,value])=>({name,value})).sort((a,b)=>b.value-a.value).slice(0,6))

    // Products
    const prodMap = {}
    ;(recentOrdersRes.data||[]).forEach(o=>{ const n=o.product_name||'Unknown'; if(!prodMap[n]) prodMap[n]={name:n,revenue:0,units:0}; prodMap[n].revenue+=Number(o.total_amount||0); prodMap[n].units+=1 })
    setTopProducts(Object.values(prodMap).sort((a,b)=>b.revenue-a.revenue).slice(0,8))

    setLoading(false)
    fetchRecentActivity()
    fetchTopCustomers()
  }

  const fetchRecentActivity = async () => {
    const { data } = await supabase.from('activity_logs').select('*').eq('seller_id',seller.id).order('created_at',{ascending:false}).limit(8)
    setRecentActivity(data||[])
  }

  const fetchTopCustomers = async () => {
    const { data } = await supabase.from('orders').select('customer_id,total_amount,customers(name,phone)').eq('seller_id',seller.id).neq('status','return')
    if (!data) return
    const map = {}
    data.forEach(o=>{ const cid=o.customer_id; if(!cid) return; if(!map[cid]) map[cid]={id:cid,name:o.customers?.name||'Unknown',phone:o.customers?.phone||'',orders:0,spent:0}; map[cid].orders+=1; map[cid].spent+=Number(o.total_amount||0) })
    setTopCustomers(Object.values(map).sort((a,b)=>b.spent-a.spent).slice(0,5))
  }

  const fmt = (n) => {
    const abs=Math.abs(Math.round(n||0))
    if(abs>=1000000) return sym+(abs/1000000).toFixed(1)+'M'
    if(abs>=100000)  return sym+Math.round(abs/1000)+'k'
    return sym+abs.toLocaleString()
  }

  const pct = (curr,prev) => {
    if (!prev) return null
    const diff=((curr-prev)/prev*100).toFixed(1)
    return { text:Math.abs(diff)+'% vs '+(curr===stats.todayRevenue||curr===stats.todayOrders?'yesterday':'last month'), up:diff>=0 }
  }

  const dashFiltered = allOrders.filter(o=>{
    if (!dashSearch) return true
    const q=dashSearch.toLowerCase()
    return o.order_code?.toLowerCase().includes(q)||o.customers?.name?.toLowerCase().includes(q)||o.customers?.phone?.includes(dashSearch)||o.product_name?.toLowerCase().includes(q)
  })
  const dashTotalPages = Math.max(1,Math.ceil(dashFiltered.length/DASH_PAGE_SIZE))
  const dashStart      = (dashPage-1)*DASH_PAGE_SIZE
  const dashPageOrders = dashFiltered.slice(dashStart,dashStart+DASH_PAGE_SIZE)
  const totalSourceOrders = sourceData.reduce((s,d)=>s+d.value,0)
  const todayRevPct = pct(stats.todayRevenue,stats.yesterdayRevenue)
  const monthRevPct = pct(stats.monthRevenue,stats.lastMonthRevenue)
  const todayOrdPct = pct(stats.todayOrders,stats.yesterdayOrders)
  const monthOrdPct = pct(stats.monthOrders,stats.lastMonthOrders)

  // Revenue goal progress
  const goalProgress = goalData?.revenue_goal ? Math.min(100, Math.round((stats.monthRevenue/goalData.revenue_goal)*100)) : null

  return (
    <div className="flex-1 overflow-x-hidden pc-page" style={{ background:'var(--bg-page)', fontFamily:'var(--font-body)' }}>
      <Topbar title="Dashboard" />

      <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:'16px' }}>

        {/* ── Page header ── */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
          <div>
            <h1 style={{ fontSize:'20px', fontWeight:800, color:'var(--text-primary)', fontFamily:'var(--font-ui)', letterSpacing:'-0.3px', margin:0 }}>
              Overview
            </h1>
            <p style={{ fontSize:'13px', color:'var(--text-muted)', margin:'3px 0 0', fontFamily:'var(--font-body)' }}>
              {new Date().toLocaleDateString('en-BD',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
            </p>
          </div>
          <button onClick={()=>navigate('/orders/add')} className="pc-btn"
            style={{ display:'flex', alignItems:'center', gap:'8px', padding:'9px 18px', borderRadius:'var(--radius-md)', background:'var(--primary)', color:'white', border:'none', cursor:'pointer', fontSize:'13px', fontWeight:600, fontFamily:'var(--font-ui)', boxShadow:'0 2px 8px rgba(14,165,233,0.3)' }}
            onMouseEnter={e=>{ e.currentTarget.style.background='var(--primary-hover)'; e.currentTarget.style.boxShadow='0 4px 12px rgba(14,165,233,0.4)' }}
            onMouseLeave={e=>{ e.currentTarget.style.background='var(--primary)'; e.currentTarget.style.boxShadow='0 2px 8px rgba(14,165,233,0.3)' }}>
            <Icon path={IC.plus} color="white" size={15} sw={2.5} />
            New Order
          </button>
        </div>

        {/* ── Motivational quote banner ── */}
        <div style={{ borderRadius:'var(--radius-lg)', padding:'13px 18px', background:'linear-gradient(135deg, #0B1F3A 0%, #1a3a6e 100%)', display:'flex', alignItems:'center', gap:'12px', boxShadow:'0 4px 16px rgba(11,31,58,0.15)' }}>
          <div style={{ width:'32px', height:'32px', borderRadius:'10px', background:'rgba(45,212,176,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <Icon path={IC.lightbulb} color="#2DD4B0" size={16} sw={2} />
          </div>
          <div style={{ fontSize:'13.5px', fontWeight:600, color:'#FFFFFF', lineHeight:1.4, fontFamily: lang==='bn' ? 'Hind Siliguri, var(--font-body)' : 'var(--font-body)' }}>
            {quote[lang] || quote.en}
          </div>
        </div>

        {/* ── Attention bar ── */}
        {(stats.pending>0||stats.lowStock>0||stats.pendingRequests>0) && (
          <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', padding:'10px 16px', borderRadius:'var(--radius-md)', background:'var(--bg-card)', border:'1px solid var(--error-border)', boxShadow:'var(--shadow-sm)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'6px', flexShrink:0 }}>
              <FlatIcon path="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" size={14} color="var(--error)" />
              <span style={{ fontSize:'12px', fontWeight:700, color:'var(--error)', fontFamily:'var(--font-ui)' }}>Needs attention</span>
            </div>
            {stats.pendingRequests>0&&<button onClick={()=>navigate('/orders',{state:{tab:'requests'}})} style={{ fontSize:'11.5px',fontWeight:500,padding:'3px 11px',borderRadius:'var(--radius-full)',background:'var(--primary-light)',border:'1px solid var(--primary-border)',color:'var(--primary)',cursor:'pointer',fontFamily:'var(--font-body)' }}>{stats.pendingRequests} customer requests</button>}
            {stats.pending>0&&<button onClick={()=>navigate('/orders')} style={{ fontSize:'11.5px',fontWeight:500,padding:'3px 11px',borderRadius:'var(--radius-full)',background:'var(--error-light)',border:'1px solid var(--error-border)',color:'var(--error)',cursor:'pointer',fontFamily:'var(--font-body)' }}>{stats.pending} orders need action</button>}
            {stats.lowStock>0&&<button onClick={()=>navigate('/inventory')} style={{ fontSize:'11.5px',fontWeight:500,padding:'3px 11px',borderRadius:'var(--radius-full)',background:'var(--warning-light)',border:'1px solid var(--warning-border)',color:'var(--warning)',cursor:'pointer',fontFamily:'var(--font-body)' }}>{stats.lowStock} low stock items</button>}
          </div>
        )}

        {/* ── Hero KPI cards ── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'12px' }} className="db-kpi-grid">
          <HeroKpiCard
            label="Today's Revenue" rawValue={stats.todayRevenue} value={fmt(stats.todayRevenue)}
            delta={todayRevPct?.text} deltaUp={todayRevPct?.up}
            gradient="linear-gradient(135deg, #0B1F3A 0%, #1a3a6e 100%)"
            icon={<svg width="17" height="17" viewBox="0 0 20 20" fill="rgba(255,255,255,0.85)"><path fillRule="evenodd" d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267zM10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/></svg>}
            loading={loading} />
          <HeroKpiCard
            label="This Month" rawValue={stats.monthRevenue} value={fmt(stats.monthRevenue)}
            delta={monthRevPct?.text} deltaUp={monthRevPct?.up}
            gradient="linear-gradient(135deg, #0284C7 0%, #0EA5E9 100%)"
            icon={<svg width="17" height="17" viewBox="0 0 20 20" fill="rgba(255,255,255,0.85)"><path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd"/></svg>}
            loading={loading} />
          <KpiCard
            label="Orders Today" rawValue={stats.todayOrders} value={String(stats.todayOrders)}
            delta={todayOrdPct?.text} deltaUp={todayOrdPct?.up}
            accentColor="#7C3AED"
            icon={<svg width="14" height="14" viewBox="0 0 20 20" fill="#7C3AED"><path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd"/></svg>}
            loading={loading} />
          <KpiCard
            label="Orders This Month" rawValue={stats.monthOrders} value={String(stats.monthOrders)}
            delta={monthOrdPct?.text} deltaUp={monthOrdPct?.up}
            accentColor="#F59E0B"
            icon={<svg width="14" height="14" viewBox="0 0 20 20" fill="#F59E0B"><path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" clipRule="evenodd"/></svg>}
            loading={loading} />
        </div>

        {/* ── Secondary metrics + quick actions ── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr 1fr', gap:'12px' }} className="db-secondary-grid">
          <MetricTile label="Avg Order Value" rawValue={stats.avgOrderValue} value={fmt(stats.avgOrderValue)} icon={<svg width="12" height="12" viewBox="0 0 20 20" fill="var(--primary)"><path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/></svg>} accentColor="var(--primary)" onClick={()=>navigate('/analytics')} loading={loading} />
          <MetricTile label="Return Rate" rawValue={stats.returnRate} value={String(stats.returnRate)} suffix="%" icon={<svg width="12" height="12" viewBox="0 0 20 20" fill="var(--error)"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/></svg>} accentColor="var(--error)" onClick={()=>navigate('/orders')} loading={loading} />
          <MetricTile label="Pending Actions" rawValue={stats.pending} value={String(stats.pending)} icon={<svg width="12" height="12" viewBox="0 0 20 20" fill="var(--warning)"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/></svg>} accentColor="var(--warning)" onClick={()=>navigate('/orders')} loading={loading} />
          <MetricTile label="Low Stock Items" rawValue={stats.lowStock} value={String(stats.lowStock)} icon={<svg width="12" height="12" viewBox="0 0 20 20" fill="#F97316"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>} accentColor="#F97316" onClick={()=>navigate('/inventory')} loading={loading} />
          <MetricTile label="New Customers" rawValue={stats.newCustomers} value={String(stats.newCustomers)} icon={<svg width="12" height="12" viewBox="0 0 20 20" fill="#2DD4B0"><path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z"/></svg>} accentColor="#2DD4B0" onClick={()=>navigate('/crm')} loading={loading} />
        </div>

        {/* ── Revenue goal progress ── */}
        {goalProgress !== null && (
          <DCard>
            <div style={{ padding:'14px 20px', display:'flex', alignItems:'center', gap:'20px', flexWrap:'wrap' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px', flexShrink:0 }}>
                <div style={{ width:'32px', height:'32px', borderRadius:'var(--radius-md)', background:'rgba(45,212,176,0.12)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="#2DD4B0"><path fillRule="evenodd" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" clipRule="evenodd"/></svg>
                </div>
                <div>
                  <div style={{ fontSize:'12px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-ui)' }}>Monthly Revenue Goal</div>
                  <div style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{fmt(stats.monthRevenue)} of {fmt(goalData.revenue_goal)} · {goalProgress}% complete</div>
                </div>
              </div>
              <div style={{ flex:1, minWidth:'200px' }}>
                <div style={{ height:'8px', background:'var(--bg-muted)', borderRadius:'4px', overflow:'hidden' }}>
                  <div style={{ height:'100%', background: goalProgress>=100?'var(--success)':goalProgress>=70?'#2DD4B0':'var(--primary)', borderRadius:'4px', width:goalProgress+'%', transition:'width 1s cubic-bezier(0.4,0,0.2,1)' }} />
                </div>
              </div>
              <TLink onClick={()=>navigate('/goals')}>{goalProgress>=100?'Goal achieved!':'View goals →'}</TLink>
            </div>
          </DCard>
        )}

        {/* ── Charts ── */}
        <div style={{ display:'grid', gridTemplateColumns:'3fr 2fr', gap:'12px' }} className="db-charts-grid">
          <DCard>
            <DCardHeader
              title="Sales Analytics"
              sub="Revenue and order volume over time"
              action={
                <div style={{ display:'flex', gap:'3px' }}>
                  {['1m','3m','6m'].map(r=>(
                    <button key={r} onClick={()=>setChartRange(r)}
                      style={{ padding:'3px 10px', borderRadius:'var(--radius-sm)', border:'none', fontSize:'11.5px', fontWeight:600, cursor:'pointer', background:chartRange===r?'var(--primary)':'var(--bg-muted)', color:chartRange===r?'white':'var(--text-muted)', fontFamily:'var(--font-ui)', transition:'var(--transition-fast)' }}>
                      {r==='1m'?'1M':r==='3m'?'3M':'6M'}
                    </button>
                  ))}
                </div>
              }
            />
            <div style={{ padding:'16px 20px' }}>
              {loading ? (
                <div style={{ height:'220px', display:'flex', alignItems:'flex-end', gap:'8px', padding:'0 8px' }}>
                  {[55,80,45,90,65,95].map((h,i)=><div key={i} className="pc-skeleton" style={{ flex:1, height:`${h}%`, borderRadius:'6px 6px 0 0' }}/>)}
                </div>
              ) : chartData.length===0 ? (
                <div style={{ height:'220px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'10px' }}>
                  <div style={{ width:'48px', height:'48px', borderRadius:'var(--radius-lg)', background:'var(--bg-muted)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <Icon path={IC.chart} color="var(--text-muted)" size={22} sw={1.5} />
                  </div>
                  <span style={{ fontSize:'13px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>No data for this period</span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={chartData} margin={{top:8,right:8,left:0,bottom:4}}>
                    <defs>
                      <linearGradient id="gr1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#0EA5E9" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="gr2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#2DD4B0" stopOpacity={0.15}/>
                        <stop offset="95%" stopColor="#2DD4B0" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
                    <XAxis dataKey="month" tick={{fontSize:11,fill:'var(--text-muted)',fontFamily:'var(--font-body)'}} axisLine={false} tickLine={false}/>
                    <YAxis tick={{fontSize:11,fill:'var(--text-muted)',fontFamily:'var(--font-body)'}} width={50} axisLine={false} tickLine={false} tickFormatter={v=>v>=1000?Math.round(v/1000)+'k':v}/>
                    <Tooltip content={<CustomTooltip sym={sym}/>}/>
                    <Area type="monotone" dataKey="revenue" stroke="#0EA5E9" strokeWidth={2.5} fill="url(#gr1)" dot={{fill:'#0EA5E9',r:4,strokeWidth:2,stroke:'var(--bg-card)'}} activeDot={{r:6,strokeWidth:2,stroke:'var(--bg-card)'}}/>
                    <Area type="monotone" dataKey="orders"  stroke="#2DD4B0" strokeWidth={2}   fill="url(#gr2)" dot={{fill:'#2DD4B0',r:3,strokeWidth:2,stroke:'var(--bg-card)'}} activeDot={{r:5,strokeWidth:2,stroke:'var(--bg-card)'}}/>
                  </AreaChart>
                </ResponsiveContainer>
              )}
              <div style={{ display:'flex', gap:'16px', marginTop:'10px' }}>
                {[{color:'#0EA5E9',label:'Revenue'},{color:'#2DD4B0',label:'Orders'}].map(l=>(
                  <div key={l.label} style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                    <div style={{ width:'18px', height:'2.5px', background:l.color, borderRadius:'2px' }}/>
                    <span style={{ fontSize:'11.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{l.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </DCard>

          {/* Source donut */}
          <DCard>
            <DCardHeader title="Orders by Source" sub="Where your orders come from"/>
            <div style={{ padding:'12px 20px 16px' }}>
              {loading ? (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'12px', padding:'12px 0' }}>
                  <Sk w={130} h={130} r={65}/>
                  {[...Array(4)].map((_,i)=><Sk key={i} w="100%" h={12}/>)}
                </div>
              ) : sourceData.length===0 ? (
                <div style={{ height:'180px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <span style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>No data yet</span>
                </div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={sourceData} cx="50%" cy="50%" innerRadius={46} outerRadius={68} paddingAngle={3} dataKey="value" strokeWidth={0} isAnimationActive animationBegin={200} animationDuration={900}>
                        {sourceData.map((entry,i)=><Cell key={entry.name} fill={CHART_COLORS[i%CHART_COLORS.length]}/>)}
                      </Pie>
                      <Tooltip contentStyle={{ border:'1px solid var(--border)', borderRadius:'8px', fontFamily:'var(--font-body)', fontSize:'11px', background:'var(--bg-card)', color:'var(--text-primary)' }}/>
                      <DonutCenter cx={150} cy={80} total={totalSourceOrders}/>
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ display:'flex', flexDirection:'column', gap:'6px', marginTop:'8px' }}>
                    {sourceData.map((src,i)=>{
                      const pctVal = totalSourceOrders?Math.round(src.value/totalSourceOrders*100):0
                      const color  = CHART_COLORS[i%CHART_COLORS.length]
                      return (
                        <div key={src.name}>
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'3px' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'7px' }}>
                              <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:color, flexShrink:0 }}/>
                              <span style={{ fontSize:'11.5px', color:'var(--text-primary)', fontFamily:'var(--font-body)', maxWidth:'90px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{src.name}</span>
                            </div>
                            <span style={{ fontSize:'11.5px', fontWeight:700, color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{pctVal}%</span>
                          </div>
                          <div style={{ height:'3px', background:'var(--bg-muted)', borderRadius:'2px', overflow:'hidden' }}>
                            <div style={{ height:'100%', background:color, borderRadius:'2px', width:pctVal+'%' }}/>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          </DCard>
        </div>

        {/* ── Quick actions + top products ── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:'12px' }} className="db-qa-grid">
          <DCard>
            <DCardHeader title="Quick Actions" sub="Common tasks"/>
            <div style={{ padding:'12px 16px', display:'flex', flexDirection:'column', gap:'8px' }}>
              <QuickAction label="Create new order" sub="Add a sale manually" icon={<Icon path={IC.plus} color="var(--primary)" size={15} sw={2}/>} accentColor="var(--primary)" onClick={()=>navigate('/orders/add')}/>
              <QuickAction label="Add product" sub="Expand your inventory" icon={<Icon path={IC.inventory} color="#2DD4B0" size={15} sw={2}/>} accentColor="#2DD4B0" onClick={()=>navigate('/inventory/add')}/>
              <QuickAction label="View analytics" sub="Full sales breakdown" icon={<Icon path={IC.analytics} color="#7C3AED" size={15} sw={2}/>} accentColor="#7C3AED" onClick={()=>navigate('/analytics')}/>
              <QuickAction label="AI Studio" sub="Generate model photos" icon={<Icon path="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" color="#F59E0B" size={15} sw={2}/>} accentColor="#F59E0B" onClick={()=>navigate('/grow/aimodel')}/>
            </div>
          </DCard>

          <DCard>
            <DCardHeader title="Top Products" sub="By revenue or units — all time" action={<TLink onClick={()=>navigate('/analytics')}>Full analytics →</TLink>}/>
            <div style={{ padding:'16px 20px' }}>
              <TopProductsChart data={topProducts} loading={loading} sym={sym} metric={productMetric} setMetric={setProductMetric}/>
            </div>
          </DCard>
        </div>

        {/* ── Bottom: orders table + right panel ── */}
        <div style={{ display:'grid', gridTemplateColumns:'3fr 2fr', gap:'12px', alignItems:'start' }} className="db-bottom-grid">

          {/* Orders table */}
          <DCard style={{ display:'flex', flexDirection:'column' }}>
            <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
                <div>
                  <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-ui)' }}>Recent Orders</div>
                  <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginTop:'2px', fontFamily:'var(--font-body)' }}>{allOrders.length} total across all time</div>
                </div>
                <button onClick={()=>navigate('/orders')}
                  style={{ fontSize:'12px', fontWeight:600, color:'var(--primary)', background:'var(--primary-light)', border:'none', padding:'5px 12px', borderRadius:'var(--radius-sm)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
                  View all orders
                </button>
              </div>
              <div style={{ position:'relative' }}>
                <input type="text" placeholder="Search orders..." value={dashSearch} onChange={e=>{setDashSearch(e.target.value);setDashPage(1)}}
                  className="pc-input"
                  style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'7px 10px 7px 34px', fontSize:'12.5px', background:'var(--bg-muted)', color:'var(--text-primary)', fontFamily:'var(--font-body)', outline:'none', boxSizing:'border-box' }}/>
                <div style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)' }}>
                  <Icon path={IC.search} color="var(--text-muted)" size={13}/>
                </div>
                {dashSearch&&<button onClick={()=>{setDashSearch('');setDashPage(1)}} style={{ position:'absolute', right:'8px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:'14px' }}>×</button>}
              </div>
            </div>

            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'560px' }}>
                <thead>
                  <tr style={{ background:'var(--bg-muted)', borderBottom:'1px solid var(--border)' }}>
                    {['#','','Order ID','Customer','Product','Amount','Date','Status'].map((h,i)=>(
                      <th key={h+i} style={{ padding:'8px '+(i===0?'8px':'12px'), textAlign:i===5?'right':'left', fontSize:'10px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:'var(--font-ui)', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? [...Array(5)].map((_,i)=>(
                    <tr key={i}><td colSpan={8} style={{ padding:'12px 20px' }}><Sk h={32}/></td></tr>
                  )) : dashFiltered.length===0 ? (
                    <tr><td colSpan={8}>
                      <div style={{ padding:'40px', textAlign:'center' }}>
                        <div style={{ width:'48px', height:'48px', borderRadius:'var(--radius-lg)', background:'var(--bg-muted)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
                          <Icon path={IC.orders} color="var(--text-muted)" size={22} sw={1.5}/>
                        </div>
                        <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)', fontFamily:'var(--font-ui)', marginBottom:'4px' }}>
                          {allOrders.length===0?'No orders yet':'No orders match your search'}
                        </div>
                        {allOrders.length===0&&<button onClick={()=>navigate('/orders/add')} style={{ marginTop:'10px', fontSize:'12.5px', fontWeight:600, padding:'8px 20px', borderRadius:'var(--radius-md)', background:'var(--primary)', color:'white', border:'none', cursor:'pointer', fontFamily:'var(--font-ui)' }}>Create first order</button>}
                      </div>
                    </td></tr>
                  ) : dashPageOrders.map((order,idx)=>{
                    const s = STATUS_STYLES[order.status]||{bg:'var(--bg-muted)',text:'var(--text-muted)'}
                    const d = new Date(order.created_at)
                    return (
                      <tr key={order.id} onClick={()=>navigate('/orders/'+order.id)} className="pc-table-row"
                        style={{ borderBottom:'1px solid var(--border)', cursor:'pointer' }}>
                        <td style={{ padding:'10px 8px', fontSize:'11px', color:'var(--text-muted)', fontFamily:'var(--font-body)', textAlign:'center', width:'32px' }}>{dashStart+idx+1}</td>
                        <td style={{ padding:'10px 4px', width:'24px' }}><SourceBadge orderSource={order.order_source}/></td>
                        <td style={{ padding:'10px 12px' }}><span style={{ fontSize:'12px', fontWeight:700, color:'var(--primary)', fontFamily:'var(--font-mono)' }}>{order.order_code}</span></td>
                        <td style={{ padding:'10px 12px', maxWidth:'130px' }}>
                          <div style={{ fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{order.customers?.name||'—'}</div>
                          <div style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{order.customers?.phone}</div>
                        </td>
                        <td style={{ padding:'10px 12px', maxWidth:'140px' }}>
                          <div style={{ fontSize:'12px', color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{order.product_name||'—'}</div>
                        </td>
                        <td style={{ padding:'10px 12px', textAlign:'right' }}>
                          <span style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-body)', whiteSpace:'nowrap' }}>{fmt(Number(order.total_amount||0))}</span>
                        </td>
                        <td style={{ padding:'10px 12px', whiteSpace:'nowrap' }}>
                          <div style={{ fontSize:'12px', fontWeight:500, color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{d.toLocaleDateString('en-BD',{day:'numeric',month:'short'})}</div>
                          <div style={{ fontSize:'10.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{d.toLocaleTimeString('en-BD',{hour:'2-digit',minute:'2-digit'})}</div>
                        </td>
                        <td style={{ padding:'10px 12px' }}>
                          <span style={{ fontSize:'10.5px', fontWeight:600, padding:'3px 9px', borderRadius:'var(--radius-full)', background:s.bg, color:s.text, whiteSpace:'nowrap' }}>
                            {STATUS_LABELS[order.status]||order.status}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{ padding:'10px 20px', borderTop:'1px solid var(--border)', background:'var(--bg-muted)', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
              <div style={{ fontSize:'11.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>
                <strong style={{ color:'var(--text-primary)' }}>{dashStart+1}–{Math.min(dashStart+DASH_PAGE_SIZE,Math.max(1,dashFiltered.length))}</strong> of <strong style={{ color:'var(--text-primary)' }}>{dashFiltered.length}</strong>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                <button onClick={()=>setDashPage(p=>Math.max(1,p-1))} disabled={dashPage===1}
                  style={{ width:'28px', height:'28px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', cursor:dashPage===1?'not-allowed':'pointer', opacity:dashPage===1?0.4:1 }}>
                  <Icon path={IC.chevL} color="var(--text-primary)" size={12} sw={2}/>
                </button>
                <span style={{ fontSize:'12px', fontWeight:600, color:'var(--text-primary)', padding:'0 8px', fontFamily:'var(--font-body)' }}>{dashPage} / {dashTotalPages}</span>
                <button onClick={()=>setDashPage(p=>Math.min(dashTotalPages,p+1))} disabled={dashPage===dashTotalPages}
                  style={{ width:'28px', height:'28px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', cursor:dashPage===dashTotalPages?'not-allowed':'pointer', opacity:dashPage===dashTotalPages?0.4:1 }}>
                  <Icon path={IC.chevR} color="var(--text-primary)" size={12} sw={2}/>
                </button>
              </div>
            </div>
          </DCard>

          {/* Right panel */}
          <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>

            {/* Low stock */}
            <DCard>
              <DCardHeader title="Low Stock Alert" sub="Items running out" action={<TLink onClick={()=>navigate('/inventory')}>View all</TLink>}/>
              {loading ? (
                <div style={{ padding:'14px 16px', display:'flex', flexDirection:'column', gap:'10px' }}>
                  {[...Array(4)].map((_,i)=><Sk key={i} h={32}/>)}
                </div>
              ) : lowStockItems.length===0 ? (
                <div style={{ padding:'20px', textAlign:'center' }}>
                  <div style={{ width:'40px', height:'40px', borderRadius:'var(--radius-md)', background:'var(--success-light)', border:'1px solid var(--success-border)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 10px' }}>
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="var(--success)"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                  </div>
                  <div style={{ fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', fontFamily:'var(--font-ui)' }}>All stock healthy</div>
                </div>
              ) : lowStockItems.map(item=>(
                <div key={item.id} onClick={()=>navigate('/inventory/'+item.id)} className="pc-table-row"
                  style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'9px 16px', borderBottom:'1px solid var(--border)', cursor:'pointer' }}>
                  <div style={{ minWidth:0, flex:1 }}>
                    <div style={{ fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{item.name}</div>
                    <div style={{ fontSize:'10.5px', color:'var(--text-muted)', fontFamily:'var(--font-mono)', marginTop:'1px' }}>{item.product_code}</div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px', flexShrink:0 }}>
                    <div style={{ width:'40px', height:'4px', background:'var(--bg-muted)', borderRadius:'2px', overflow:'hidden' }}>
                      <div style={{ height:'100%', background:item.stock===0?'var(--error)':item.stock<=2?'var(--error)':'var(--warning)', width:item.stock===0?'100%':Math.min((item.stock/10)*100,100)+'%' }}/>
                    </div>
                    <span style={{ fontSize:'11px', fontWeight:700, padding:'2px 8px', borderRadius:'var(--radius-full)', background:item.stock===0?'var(--error-light)':'var(--warning-light)', color:item.stock===0?'var(--error)':'var(--warning)', minWidth:'42px', textAlign:'center' }}>
                      {item.stock===0?'Out':item.stock+' left'}
                    </span>
                  </div>
                </div>
              ))}
            </DCard>

            {/* Top customers */}
            <DCard>
              <DCardHeader title="Top Customers" sub="All-time by spend" action={<TLink onClick={()=>navigate('/crm')}>View all</TLink>}/>
              {loading ? (
                <div style={{ padding:'14px 16px', display:'flex', flexDirection:'column', gap:'10px' }}>
                  {[...Array(4)].map((_,i)=><Sk key={i} h={36}/>)}
                </div>
              ) : topCustomers.length===0 ? (
                <div style={{ padding:'20px', textAlign:'center', fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>No customer data yet</div>
              ) : topCustomers.map((c,i)=>(
                <div key={c.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 16px', borderBottom:i<topCustomers.length-1?'1px solid var(--border)':'none' }}>
                  {/* Rank */}
                  <div style={{ width:'22px', height:'22px', borderRadius:'6px', background:i===0?'#FEF3C7':i===1?'#F1F5F9':i===2?'#FEF9EE':'var(--bg-muted)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <span style={{ fontSize:'11px', fontWeight:800, color:i===0?'#D97706':i===1?'#64748B':i===2?'#B45309':'var(--text-muted)', fontFamily:'var(--font-ui)' }}>{i+1}</span>
                  </div>
                  {/* Avatar */}
                  <div style={{ width:'30px', height:'30px', borderRadius:'50%', background:'var(--primary-light)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11.5px', fontWeight:700, color:'var(--primary)', flexShrink:0, fontFamily:'var(--font-ui)' }}>
                    {c.name.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{c.name}</div>
                    <div style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{c.orders} order{c.orders!==1?'s':''}</div>
                  </div>
                  <div style={{ fontSize:'13px', fontWeight:700, color:'var(--primary)', flexShrink:0, fontFamily:'var(--font-body)' }}>{fmt(c.spent)}</div>
                </div>
              ))}
            </DCard>

            <AdBoostCard sellerId={seller?.id} navigate={navigate}/>

            {/* Recent activity */}
            <DCard>
              <DCardHeader title="Recent Activity" sub="Last 8 actions" action={<TLink onClick={()=>navigate('/settings?tab=activity')}>See all</TLink>}/>
              {loading ? (
                <div style={{ padding:'14px 16px', display:'flex', flexDirection:'column', gap:'10px' }}>
                  {[...Array(4)].map((_,i)=><Sk key={i} h={32}/>)}
                </div>
              ) : recentActivity.length===0 ? (
                <div style={{ padding:'20px', textAlign:'center', fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>No activity logged yet</div>
              ) : recentActivity.map((log,i)=>{
                const meta = ACTIVITY_META[log.action]||{color:'var(--text-muted)',label:log.action}
                return (
                  <div key={log.id} style={{ display:'flex', alignItems:'flex-start', gap:'10px', padding:'9px 16px', borderBottom:i<recentActivity.length-1?'1px solid var(--border)':'none' }}>
                    <div style={{ width:'28px', height:'28px', borderRadius:'7px', background:meta.color+'18', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', marginTop:'1px' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={meta.color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                      </svg>
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', fontFamily:'var(--font-ui)' }}>{meta.label}</div>
                      {log.new_value&&log.new_value.length<25&&<div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'2px', fontFamily:'var(--font-body)' }}>{log.entity_type} → {log.new_value}</div>}
                    </div>
                    <div style={{ fontSize:'10.5px', color:'var(--text-muted)', flexShrink:0, fontFamily:'var(--font-body)', marginTop:'2px' }}>{getTimeAgo(new Date(log.created_at))}</div>
                  </div>
                )
              })}
            </DCard>
          </div>
        </div>
      </div>

      <style>{`
        .db-kpi-grid        { }
        .db-secondary-grid  { }
        .db-charts-grid     { }
        .db-qa-grid         { }
        .db-bottom-grid     { }
        @media(max-width:1100px){
          .db-kpi-grid       { grid-template-columns:1fr 1fr!important }
          .db-secondary-grid { grid-template-columns:1fr 1fr 1fr!important }
          .db-bottom-grid    { grid-template-columns:1fr!important }
          .db-qa-grid        { grid-template-columns:1fr!important }
        }
        @media(max-width:768px){
          .db-kpi-grid,.db-secondary-grid,.db-charts-grid,.db-qa-grid,.db-bottom-grid{ grid-template-columns:1fr!important }
        }
      `}</style>
    </div>
  )
}

export default Dashboard